import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

export const surveyResponseSchema = z.object({
  inquiry_id: z.string().uuid().nullable().optional(),
  name: z.string().trim().max(200).nullable().optional(),
  email: z.string().trim().max(255).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  answers: z.record(z.string(), z.union([z.string(), z.array(z.string())])),
  scores: z.record(z.string(), z.number()),
  total_score: z.number().int().min(0).max(100).nullable().optional(),
  tier: z.enum(["3", "2", "DQ"]),
  routing_outcome: z.enum(["demo", "intro", "disqualified"]),
  dq_reason: z.string().max(300).nullable().optional(),
  variant_id: z.string().uuid().nullable().optional(),
  variant_label: z.enum(["A", "B", "C", "D"]).nullable().optional(),
  session_id: z.string().max(120).nullable().optional(),
  landing_path: z.string().max(1000).nullable().optional(),
  path: z.string().max(1000).nullable().optional(),
  referrer: z.string().max(1000).nullable().optional(),
});

export type SurveyResponseInput = z.infer<typeof surveyResponseSchema>;

/**
 * Persist a survey response. Called for DISQUALIFIED respondents (who never
 * reach the contact step, so they never create an inquiry) and reused by the
 * lead submit path for qualified/maybe leads.
 */
export async function insertSurveyResponse(
  input: SurveyResponseInput,
  userAgent: string | null,
) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  let variantId = input.variant_id ?? null;
  if (!variantId && input.variant_label) {
    const { data } = await supabaseAdmin
      .from("landing_page_variants")
      .select("id")
      .eq("variant_label", input.variant_label)
      .maybeSingle();
    variantId = data?.id ?? null;
  }
  const { error } = await supabaseAdmin.from("survey_responses").insert({
    inquiry_id: input.inquiry_id ?? null,
    name: input.name ?? null,
    email: input.email ?? null,
    phone: input.phone ?? null,
    answers: input.answers as never,
    scores: input.scores as never,
    total_score: input.total_score ?? null,
    tier: input.tier,
    routing_outcome: input.routing_outcome,
    dq_reason: input.dq_reason ?? null,
    variant_id: variantId,
    variant_label: input.variant_label ?? null,
    session_id: input.session_id ?? null,
    landing_path: input.landing_path ?? null,
    path: input.path ?? null,
    referrer: input.referrer ?? null,
    user_agent: userAgent,
  });
  if (error) {
    console.error("[survey_responses] insert failed", error);
    return { ok: false as const, error: error.message };
  }
  return { ok: true as const };
}

/** Public: records a response (used for disqualified respondents). */
export const recordSurveyResponse = createServerFn({ method: "POST" })
  .inputValidator((i) => surveyResponseSchema.parse(i))
  .handler(async ({ data }) => {
    const ua = getRequestHeader("user-agent") ?? null;
    return insertSurveyResponse(data, ua);
  });

/** Admin: list recent survey responses for the admin Survey tab. */
export const listSurveyResponses = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => z.object({ limit: z.number().int().min(1).max(500).default(100) }).parse(i))
  .handler(async ({ context, data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: role, error: roleError } = await supabaseAdmin
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId)
      .eq("role", "admin")
      .maybeSingle();
    if (roleError) throw new Error(roleError.message);
    if (!role) throw new Error("Forbidden: admin role required");

    const { data: rows, error } = await supabaseAdmin
      .from("survey_responses")
      .select(
        "id, created_at, name, email, phone, tier, routing_outcome, dq_reason, total_score, scores, answers, variant_label, landing_path, path, inquiry_id",
      )
      .order("created_at", { ascending: false })
      .limit(data.limit);
    if (error) throw new Error(error.message);
    return rows ?? [];
  });
